import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography } from "@mui/material";
import { useDrag, useDrop } from "react-dnd";
import { contrastText } from "./color";
import { FORMATION_DRAG_TYPE, } from "./types";
const GK_COLOUR = "#22c55e";
const EMPTY_BG = "rgba(15, 23, 42, 0.55)";
const EMPTY_BORDER = "rgba(255, 255, 255, 0.55)";
export const PitchSlot = ({ slot, player, primaryColour, onDrop, isSelected = false, onClick, }) => {
    const isGK = slot.band === "GK";
    const shirtColour = isGK ? GK_COLOUR : primaryColour;
    const bgColour = player ? shirtColour : EMPTY_BG;
    const positionTextColour = player ? contrastText(shirtColour) : "#ffffff";
    const [{ isDragging }, dragRef] = useDrag(() => ({
        type: FORMATION_DRAG_TYPE,
        item: player
            ? { source: "pitch", player, slotId: slot.id }
            : {},
        canDrag: !!player,
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }), [player, slot.id]);
    const [{ isOver, canDrop }, dropRef] = useDrop(() => ({
        accept: FORMATION_DRAG_TYPE,
        canDrop: (item) => !(item.source === "pitch" && item.slotId === slot.id),
        drop: (item) => {
            onDrop(item, slot.id);
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    }), [slot.id, onDrop]);
    const setRefs = (node) => {
        dragRef(node);
        dropRef(node);
    };
    const isHighlighted = isOver && canDrop;
    return (_jsxs(Box, { ref: setRefs, "data-testid": `pitch-slot-${slot.id}`, role: "button", tabIndex: 0, "aria-pressed": isSelected, "aria-label": player
            ? `${slot.label}: #${player.squadNumber ?? "?"} ${player.lastName}`
            : `${slot.label} empty slot`, onClick: (e) => {
            e.stopPropagation();
            onClick?.(slot.id, player);
        }, sx: {
            position: "absolute",
            left: `${slot.x}%`,
            top: `${slot.y}%`,
            transform: "translate(-50%, -50%)",
            width: "18%",
            aspectRatio: "1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: player ? "grab" : "pointer",
            opacity: isDragging ? 0.3 : 1,
            userSelect: "none",
            // Invisible hit-extender — the visible circle is the inner box.
            backgroundColor: "transparent",
        }, children: [_jsx(Box, { sx: {
                    width: "61%",
                    aspectRatio: "1",
                    borderRadius: "50%",
                    backgroundColor: bgColour,
                    border: `2px solid ${EMPTY_BORDER}`,
                    boxShadow: isHighlighted
                        ? `0 0 0 4px rgba(255,255,255,0.85)`
                        : isSelected
                            ? `0 0 0 4px ${primaryColour}, 0 0 0 6px rgba(255,255,255,0.95)`
                            : "0 1px 4px rgba(0,0,0,0.45)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: positionTextColour,
                    transition: "box-shadow 0.1s ease",
                }, children: _jsx(Typography, { sx: {
                        fontSize: "min(1.9vw, 11px)",
                        fontWeight: 800,
                        letterSpacing: 0.5,
                        lineHeight: 1,
                    }, children: slot.label }) }), player && (_jsx(Typography, { sx: {
                    position: "absolute",
                    top: "100%",
                    left: "50%",
                    transform: "translate(-50%, -25%)",
                    color: "#ffffff",
                    fontSize: "clamp(11px, 2.4vw, 15px)",
                    fontWeight: 700,
                    // Translucent black pill so the name reads cleanly when it
                    // overlaps pitch markings (penalty boxes, halfway line, etc.).
                    backgroundColor: "rgba(0, 0, 0, 0.55)",
                    px: 0.75,
                    py: 0.125,
                    borderRadius: 0.75,
                    textShadow: "0 1px 2px rgba(0,0,0,0.85)",
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                    letterSpacing: 0.2,
                }, children: player.lastName }))] }));
};
